import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  X, Instagram, Facebook, Loader2, Send, CheckCircle2, AlertTriangle,
  ExternalLink, RefreshCw, SkipForward,
} from "lucide-react";

/**
 * SocialBatchPublishModal — Fase 3.
 * Recibe una lista de placas (image + article) y publica secuencialmente en
 * las plataformas elegidas. Muestra progreso "X de Y" y resultado consolidado
 * con opción "Reintentar fallidas".
 *
 * Props:
 *   - items: [{image, article}]
 *   - onClose(): void
 */
export default function SocialBatchPublishModal({ items, onClose }) {
  const [platforms, setPlatforms] = useState(["instagram", "facebook"]);
  const [starting, setStarting] = useState(false);
  const [batch, setBatch] = useState(null); // { batch_id, total, ... }
  const [status, setStatus] = useState(null); // fetched status doc
  const [retrying, setRetrying] = useState(false);

  const togglePlatform = (p) => {
    setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const startBatch = async () => {
    if (!platforms.length) {
      toast.error("Elegí al menos una plataforma");
      return;
    }
    setStarting(true);
    try {
      const body = {
        items: items.map((it) => ({
          image_id: it.image.id,
          caption: buildCaption(it.article),
        })),
        platforms,
        force: false,
      };
      const res = await api.adminSocialBatchPublish(body);
      setBatch(res);
      setStatus({ status: "running", total: res.total, done: 0, counts: {}, results: [] });
      toast.success(`Lote iniciado · ${res.total} publicaciones`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo iniciar el lote");
    } finally {
      setStarting(false);
    }
  };

  // Poll status while running
  useEffect(() => {
    if (!batch?.batch_id || status?.status === "finished") return;
    const t = setInterval(async () => {
      try {
        const doc = await api.adminSocialBatchStatus(batch.batch_id);
        setStatus(doc);
        if (doc.status === "finished") clearInterval(t);
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [batch, status?.status]);

  const retryFailed = async () => {
    if (!batch?.batch_id) return;
    setRetrying(true);
    try {
      const res = await api.adminSocialBatchRetryFailed(batch.batch_id);
      if (res.batch_id) {
        setBatch(res);
        setStatus({ status: "running", total: res.total, done: 0, counts: {}, results: [] });
        toast.success("Reintento iniciado");
      } else {
        toast.info(res.message || "Nada para reintentar");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo reintentar");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      data-testid="social-batch-modal"
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-nightblue text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold">Publicar lote en Redes Sociales</div>
            <div className="text-[10px] uppercase tracking-widest text-white/70">
              {items.length} placa(s) seleccionada(s)
            </div>
          </div>
          <button onClick={onClose} data-testid="social-batch-close" className="text-white/80 hover:text-watergreen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Platform selector (only visible before starting) */}
          {!batch && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2">Plataformas</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => togglePlatform("instagram")}
                    data-testid="batch-platform-instagram"
                    className={`flex-1 border p-3 flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest transition-colors ${
                      platforms.includes("instagram")
                        ? "border-pink-500 bg-pink-50 text-pink-800"
                        : "border-softblue text-textgray hover:bg-lightgray"
                    }`}
                  >
                    <Instagram className="w-4 h-4" /> Instagram
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePlatform("facebook")}
                    data-testid="batch-platform-facebook"
                    className={`flex-1 border p-3 flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest transition-colors ${
                      platforms.includes("facebook")
                        ? "border-[#1877F2] bg-blue-50 text-[#1877F2]"
                        : "border-softblue text-textgray hover:bg-lightgray"
                    }`}
                  >
                    <Facebook className="w-4 h-4" /> Facebook
                  </button>
                </div>
              </div>

              <div className="border border-softblue bg-lightgray/40 p-3 text-xs text-textgray">
                <div className="font-semibold text-blueblack mb-1">Antes de publicar</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Se generará automáticamente el caption desde título + bajada + hashtags.</li>
                  <li>Placas Story (1080×1920) sólo se publican en Instagram (FB usa otra API).</li>
                  <li>Se detectan duplicados por noticia + plataforma + formato — no se re-publican.</li>
                  <li>Cola secuencial: 1 activa por plataforma para respetar límites de Meta.</li>
                </ul>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-softblue">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  data-testid="social-batch-cancel"
                  className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={startBatch}
                  disabled={starting || !platforms.length}
                  data-testid="social-batch-start"
                  className="h-9 rounded-none bg-turquoise hover:bg-nightblue text-white text-[11px] uppercase tracking-widest font-bold px-4"
                >
                  {starting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Iniciar lote
                </Button>
              </div>
            </>
          )}

          {/* Running / result */}
          {batch && status && (
            <>
              {/* Progress bar */}
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="uppercase tracking-widest text-[10px] font-bold text-blueblack">
                    {status.status === "finished" ? "Finalizado" : `Publicando ${status.done || 0} de ${status.total || batch.total}`}
                  </div>
                  <div className="font-mono text-textgray">
                    {status.total ? Math.round(((status.done || 0) / status.total) * 100) : 0}%
                  </div>
                </div>
                <div className="w-full bg-lightgray h-2 border border-softblue">
                  <div
                    data-testid="batch-progress-bar"
                    className={`h-full ${status.status === "finished" ? "bg-emerald-500" : "bg-turquoise"} transition-all`}
                    style={{ width: `${status.total ? ((status.done || 0) / status.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Counts */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="border border-emerald-300 bg-emerald-50 p-2 text-emerald-800">
                  <div className="uppercase tracking-widest text-[10px] font-bold">Publicadas</div>
                  <div className="font-mono text-lg" data-testid="batch-count-published">{(status.counts?.published || 0) + (status.counts?.test_mode || 0)}</div>
                </div>
                <div className="border border-red-300 bg-red-50 p-2 text-red-800">
                  <div className="uppercase tracking-widest text-[10px] font-bold">Fallidas</div>
                  <div className="font-mono text-lg" data-testid="batch-count-failed">{status.counts?.failed || 0}</div>
                </div>
                <div className="border border-amber-300 bg-amber-50 p-2 text-amber-800">
                  <div className="uppercase tracking-widest text-[10px] font-bold">Omitidas</div>
                  <div className="font-mono text-lg" data-testid="batch-count-skipped">{status.counts?.skipped || 0}</div>
                </div>
                <div className="border border-softblue bg-lightgray p-2 text-textgray">
                  <div className="uppercase tracking-widest text-[10px] font-bold">Total</div>
                  <div className="font-mono text-lg">{status.total || batch.total}</div>
                </div>
              </div>

              {/* Per-item results (only when we have some) */}
              {Array.isArray(status.results) && status.results.length > 0 && (
                <div className="border border-softblue bg-white">
                  <div className="px-3 py-2 border-b border-softblue text-[10px] uppercase tracking-widest font-bold text-blueblack">
                    Detalle
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-softblue">
                    {status.results.map((r, i) => {
                      const ok = r.status === "published" || r.status === "test_mode";
                      const skipped = r.status === "skipped";
                      return (
                        <div key={i} className="px-3 py-2 text-xs flex items-start gap-2">
                          {ok ? (
                            <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                          ) : skipped ? (
                            <SkipForward className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="font-bold uppercase mr-2">{r.platform}</span>
                            <span className="font-mono text-[10px] text-textgray">{r.image_id?.slice(0, 8)}</span>
                            {" · "}
                            <span className={ok ? "text-emerald-700" : skipped ? "text-amber-700" : "text-red-700"}>
                              {r.status === "published" && "publicado"}
                              {r.status === "test_mode" && "simulado (modo prueba)"}
                              {r.status === "skipped" && `omitido — ${r.error_code}`}
                              {r.status === "failed" && `${r.error_code} — ${r.error_message}`}
                            </span>
                            {r.permalink && (
                              <a
                                href={r.permalink} target="_blank" rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center gap-0.5 underline text-nightblue hover:text-turquoise"
                              >
                                ver <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-softblue">
                <div className="text-[10px] uppercase tracking-widest text-textgray">
                  Batch ID: <span className="font-mono">{batch.batch_id?.slice(0, 8)}</span>
                  {status.test_mode && <span className="ml-3 text-amber-700 font-bold">MODO PRUEBA</span>}
                </div>
                <div className="flex items-center gap-2">
                  {status.status === "finished" && (status.counts?.failed || 0) > 0 && (
                    <Button
                      type="button"
                      onClick={retryFailed}
                      disabled={retrying}
                      data-testid="batch-retry-failed"
                      variant="outline"
                      className="h-9 rounded-none border-amber-500 text-amber-800 hover:bg-amber-50 text-[11px] uppercase tracking-widest font-bold"
                    >
                      {retrying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                      Reintentar fallidas
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={onClose}
                    data-testid="batch-close-done"
                    className="h-9 rounded-none bg-nightblue hover:bg-blueblack text-white text-[11px] uppercase tracking-widest font-bold px-4"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function buildCaption(article) {
  if (!article) return "";
  const t = article.title || "";
  const s = article.summary || "";
  const cat = article.main_category_name || "";
  const city = article.city || "";
  const clean = (x) =>
    (x || "")
      .replace(/\s+/g, "")
      .replace(/[áàä]/gi, "a").replace(/[éèë]/gi, "e").replace(/[íìï]/gi, "i")
      .replace(/[óòö]/gi, "o").replace(/[úùü]/gi, "u").replace(/ñ/gi, "n");
  const tags = [
    "#SurPrimicia", "#TierraDelFuego",
    city ? `#${clean(city)}` : null,
    cat ? `#${clean(cat)}` : null,
  ].filter(Boolean).join(" ");
  return [t, "", s, "", "Más información en surprimicia.com.ar", "", tags].join("\n");
}
